import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { shouldNoIndexRoute } from "@/lib/noindex-patterns";

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://havencostguide.com";
const SITE_NAME = "HavenCostGuide";
const DEFAULT_OG_IMAGE =
  "https://images.pexels.com/photos/1599791/pexels-photo-1599791.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200&h=630";
const SCRIPT_FLAG = "data-seo-jsonld";

function setMeta(name, content, attr = "name") {
  if (!content) {
    // Remove the tag if content is now empty (avoids stale OG/article tags between page nav)
    const stale = document.querySelector(`meta[${attr}="${name}"]`);
    if (stale) stale.remove();
    return;
  }
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(url) {
  if (!url) return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
  setMeta("og:url", url, "property");
}

function setRobots(noindex) {
  // For Discover eligibility, default to max-image-preview:large + max-snippet:-1
  // when the page is indexable. Discover prioritizes large hero images, and
  // without max-image-preview:large the image won't render at full size.
  const desired = noindex
    ? "noindex,follow"
    : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";
  let el = document.querySelector('meta[name="robots"]');
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "robots");
    document.head.appendChild(el);
  }
  el.setAttribute("content", desired);
}

function clearStructuredData() {
  document.querySelectorAll(`script[${SCRIPT_FLAG}]`).forEach((n) => n.remove());
}

function injectStructuredData(blocks) {
  clearStructuredData();
  if (!blocks) return;
  const list = Array.isArray(blocks) ? blocks : [blocks];
  list.forEach((data, idx) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute(SCRIPT_FLAG, String(idx));
    s.textContent = JSON.stringify(data);
    document.head.appendChild(s);
  });
}

export function buildCanonical(path = "/") {
  if (path.startsWith("http")) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * SEO controller for a single page.
 * - `path`: explicit canonical path. If omitted, falls back to current pathname (self-canonical).
 * - `noindex`: when true, emits robots noindex,follow.
 * - `image`: absolute URL of the share image (Open Graph + Twitter card + Pinterest Rich Pins).
 * - `type`: og:type value — "website" (default) or "article" for guides.
 * - `publishedTime`: ISO date for article:published_time (Pinterest Article Rich Pins read this).
 * - `author`: article author name for article:author.
 */
export default function SEO({
  title,
  description,
  path,
  structuredData,
  noindex,
  image,
  imageWidth = 1200,
  imageHeight = 630,
  type = "website",
  publishedTime,
  modifiedTime,
  author,
  section,
  tags,
}) {
  const { pathname } = useLocation();
  // Strip query strings and hash fragments from the fallback canonical.
  // Shared URLs (?utm_source=email, #section, etc.) must canonicalize to the
  // bare pathname — otherwise Google flags every variant as "Alternative page
  // with proper canonical tag" in GSC. Pages can still override via `path` prop.
  const effectivePath = path || pathname;
  const canonical = buildCanonical(effectivePath);
  // Auto-apply noindex,follow to routes that were pruned from the sitemap
  // during the 2026-02 SEO rescue. See /lib/noindex-patterns.js for the
  // full pattern list. Individual pages can still opt-in via the `noindex`
  // prop; this only ADDS to that, never removes it.
  const effectiveNoIndex = !!noindex || shouldNoIndexRoute(effectivePath);
  const jsonStr = structuredData ? JSON.stringify(structuredData) : "";
  // Open Graph / Pinterest Rich Pins / X cards all prefer absolute URLs.
  // Pages pass either an absolute URL or a relative path (/og/foo.jpg) — absolutize
  // the latter so the meta tags emitted during hydration match what the static
  // build-time prerender script (scripts/prerender-meta.js) writes.
  const rawImage = image || DEFAULT_OG_IMAGE;
  const ogImage = rawImage.startsWith("http") ? rawImage : `${SITE_URL}${rawImage}`;
  const tagsKey = Array.isArray(tags) ? tags.join("|") : "";

  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      setMeta("description", description);
      setMeta("og:title", title || "", "property");
      setMeta("og:description", description, "property");
      setMeta("twitter:title", title || "");
      setMeta("twitter:description", description);
    }
    setCanonical(canonical);
    setRobots(effectiveNoIndex);

    // Open Graph + Pinterest Rich Pin metadata
    setMeta("og:type", type, "property");
    setMeta("og:site_name", SITE_NAME, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:image:width", String(imageWidth), "property");
    setMeta("og:image:height", String(imageHeight), "property");
    setMeta("og:image:alt", title || SITE_NAME, "property");
    setMeta("twitter:image", ogImage);
    setMeta("twitter:card", "summary_large_image");

    // Article-specific (cleared automatically by setMeta when value is empty)
    setMeta("article:published_time", type === "article" ? publishedTime : "", "property");
    setMeta(
      "article:modified_time",
      type === "article" ? (modifiedTime || publishedTime || "") : "",
      "property"
    );
    setMeta("article:author", type === "article" ? author || SITE_NAME : "", "property");
    setMeta("article:section", type === "article" ? section || "" : "", "property");

    // article:tag is multi-valued — remove any existing tags first, then add fresh.
    document.querySelectorAll('meta[property="article:tag"]').forEach((n) => n.remove());
    if (type === "article" && Array.isArray(tags)) {
      tags.forEach((t) => {
        if (!t) return;
        const el = document.createElement("meta");
        el.setAttribute("property", "article:tag");
        el.setAttribute("content", t);
        document.head.appendChild(el);
      });
    }

    injectStructuredData(noindex ? null : structuredData);
    return () => clearStructuredData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title, description, canonical, jsonStr, noindex, ogImage,
    imageWidth, imageHeight, type, publishedTime, modifiedTime,
    author, section, tagsKey,
  ]);

  return null;
}

export { SITE_URL };
